import {inject, Injectable} from "@angular/core";
import {Action, Store} from "@ngrx/store";
import {
  selectIsClusterMode,
  selectMapActionsPolicy,
  selectOriginalMarkersGeoJson,
  selectUserMap
} from "@core/ngrx/selectors/aux/map.selectors";
import { FeatureCollection } from 'geojson';
import {combineLatest, firstValueFrom, map, mergeMap, Observable, take} from "rxjs";
import {DatacenterV2} from "@core/model/datacenter-v2";
import {mapActions} from "@core/ngrx/actions";
import {Actions, ofType} from "@ngrx/effects";
import {MapService} from "@core/service/aux/map.service";
import {MapActions} from "@core/model/aux/map-actions-policy.model";
import {UserMap} from "@core/model/user-map";

@Injectable({providedIn: 'root'})
export class MapFacade {
  private store = inject(Store)
  private actions$ = inject(Actions);
  private mapService: MapService = inject(MapService);

  readonly mapActionsPolicy$ = this.store.select(selectMapActionsPolicy)

  readonly vm$ = combineLatest([
    this.mapActionsPolicy$
  ]).pipe(
    map(([mapActionsPolicies]) => ({
      mapActionsPolicies,
    }))
  )

  createNewDatacenter() {
    this.store.dispatch(mapActions.addNewDatacenter())
  }

  mapboxMapLoaded() {
    this.store.dispatch(mapActions.mapboxMapLoaded())
  }

  loadUserMap(mapId: string, full: boolean) {
    this.store.dispatch(mapActions.loadUserMap({mapId, full}));
  }

  getCurrentUserMap(): Promise<UserMap> {
    return firstValueFrom(
      this.store.select(selectUserMap).pipe(take(1))
    )
  }

  toggleMapAction(mapAction: MapActions) {
    this.store.dispatch(mapActions.toggleMapAction({mapAction: mapAction}));
  }

  listenToCreateNewDatacenter$(): Observable<DatacenterV2> {
    return this.actions$.pipe(
      ofType(mapActions.addNewDatacenter),
      map((_: Action) => this.mapService.buildInitialDatacenter())
    );
  }

  listenToMapboxMapLoaded$(): Observable<any> {
    return this.actions$.pipe(
      ofType(mapActions.mapboxMapLoaded),
    )
  }

  toggleClusterMode(value: boolean) {
    this.store.dispatch(mapActions.toggleClusterMode({cluster: value}));
  }

  saveOriginalMarkersGeoJson(geoJson: FeatureCollection) {
    this.store.dispatch(mapActions.saveOriginalMarkersGeoJson({ geoJson }));
  }

  readonly originalMarkersGeoJson$: Observable<FeatureCollection | null> = this.store.select(selectOriginalMarkersGeoJson);

  readonly isClusterMode$: Observable<boolean> = this.store.select(selectIsClusterMode);

}
